import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

const KnownSwimsContext = createContext(null);

export const KnownSwimsProvider = ({ children }) => {
  const [swimDocuments, setSwimDocuments] = useState([]);
  const [researchBySwimId, setResearchBySwimId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const researchUnsubscribesRef = useRef(new Map());
  const pendingInitialResearchRef = useRef(new Set());

  const completeInitialResearchLoad = useCallback((knownSwimId) => {
    pendingInitialResearchRef.current.delete(knownSwimId);
    if (pendingInitialResearchRef.current.size === 0) setLoading(false);
  }, []);

  const knownSwims = useMemo(() => swimDocuments.map((swim) => ({
    ...swim,
    ...(researchBySwimId[swim.id] || { hasResearch: false, areaResearch: null })
  })), [researchBySwimId, swimDocuments]);

  const ensureKnownSwims = useCallback(() => {
    if (unsubscribeRef.current) return;

    setLoading(true);
    setError(null);
    unsubscribeRef.current = onSnapshot(
      collection(db, 'KnownSwims'),
      (snapshot) => {
        const currentIds = new Set(snapshot.docs.map((knownSwim) => knownSwim.id));
        setSwimDocuments(snapshot.docs.map((knownSwim) => ({
          id: knownSwim.id,
          ...knownSwim.data()
        })));

        researchUnsubscribesRef.current.forEach((unsubscribe, knownSwimId) => {
          if (currentIds.has(knownSwimId)) return;
          unsubscribe();
          researchUnsubscribesRef.current.delete(knownSwimId);
          pendingInitialResearchRef.current.delete(knownSwimId);
          setResearchBySwimId((current) => Object.fromEntries(
            Object.entries(current).filter(([id]) => id !== knownSwimId)
          ));
        });

        snapshot.docs.forEach((knownSwim) => {
          if (researchUnsubscribesRef.current.has(knownSwim.id)) return;

          pendingInitialResearchRef.current.add(knownSwim.id);
          const researchRef = doc(db, 'KnownSwims', knownSwim.id, 'research', 'area');
          const unsubscribe = onSnapshot(
            researchRef,
            (researchSnapshot) => {
              setResearchBySwimId((current) => ({
                ...current,
                [knownSwim.id]: {
                  hasResearch: researchSnapshot.exists(),
                  areaResearch: researchSnapshot.exists() ? researchSnapshot.data() : null
                }
              }));
              completeInitialResearchLoad(knownSwim.id);
            },
            (listenerError) => {
              console.error('Unable to listen for known swim research:', listenerError);
              setError('Unable to load known swim research.');
              completeInitialResearchLoad(knownSwim.id);
            }
          );
          researchUnsubscribesRef.current.set(knownSwim.id, unsubscribe);
        });

        if (pendingInitialResearchRef.current.size === 0) setLoading(false);
      },
      (listenerError) => {
        console.error('Unable to listen for known swims:', listenerError);
        setError('Unable to load known swims.');
        setLoading(false);
      }
    );
  }, [completeInitialResearchLoad]);

  useEffect(() => () => {
    unsubscribeRef.current?.();
    researchUnsubscribesRef.current.forEach((unsubscribe) => unsubscribe());
  }, []);

  return (
    <KnownSwimsContext.Provider value={{ knownSwims, loading, error, ensureKnownSwims }}>
      {children}
    </KnownSwimsContext.Provider>
  );
};

export const useKnownSwims = () => {
  const context = useContext(KnownSwimsContext);
  if (!context) throw new Error('useKnownSwims must be used within KnownSwimsProvider.');
  return context;
};
